// client/src/components/pages/PeriodSummary/GroupDetail.js
//
// The inline detail panel under an expanded Period Summary row (ST-00174):
// breakdown tabs (product / route / day / haulier) plus the underlying tickets.
//
// UX pass: the panel opens on DAY. Once the table row already shows the totals
// and an activity sparkline, the first question about a key is almost always
// "which days?", and the Day tab is the only one that answers it with a chart.

import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FaFilter } from 'react-icons/fa';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useThemeTokens } from '../../../utils/themeTokens';
import TicketsTable from './TicketsTable';
import { RechartsDayTooltip } from './DailyChartTooltip';
import {
  DISPATCH_COLOR,
  RECEIPT_COLOR,
  formatCount,
  formatTonnes,
  formatPct,
  formatAxisNumber,
  formatDayShort,
  variancePctOf,
  varianceToneClass,
  routeLabel,
  isGroupingRoute,
} from './utils';

const ALL_TABS = [
  { id: 'day', label: 'Day' },
  { id: 'product', label: 'Product' },
  { id: 'route', label: 'Route' },
  { id: 'haulier', label: 'Haulier' },
  { id: 'tickets', label: 'Tickets' },
];

const DEFAULT_TAB = 'day';

/** Column-wise sum of the breakdown rows, for the footer total line. */
const sumRows = (rows, field) => rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);

const BreakdownTable = ({ by, rows, onFilterRoute }) => {
  const labelHeader =
    by === 'day' ? 'Day' : by === 'route' ? 'Route' : by === 'haulier' ? 'Haulier' : 'Product';

  const totals = {
    tickets: sumRows(rows, 'tickets'),
    dispatchTickets: sumRows(rows, 'dispatchTickets'),
    receiptTickets: sumRows(rows, 'receiptTickets'),
    nettTonnes: sumRows(rows, 'nettTonnes'),
    dispatchTonnes: sumRows(rows, 'dispatchTonnes'),
    receiptTonnes: sumRows(rows, 'receiptTonnes'),
  };
  const totalVariance = totals.receiptTonnes - totals.dispatchTonnes;
  const totalPct = variancePctOf(totalVariance, totals.dispatchTonnes);

  const num = 'px-3 py-1.5 text-right font-mono tabular-nums';

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-gray-50 text-left">
            <th scope="col" className="px-3 py-2 font-medium text-gray-500">
              {labelHeader}
            </th>
            {['Tickets', 'D / R', 'Nett t', 'Dispatch t', 'Receipt t', 'Variance t'].map((h) => (
              <th key={h} scope="col" className="px-3 py-2 font-medium text-gray-500 text-right">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const pct = variancePctOf(row.varianceTonnes, row.dispatchTonnes);
            // Route rows are the group's Route Groupings; a row with no
            // groupingId is the raw legs of tickets on no grouping.
            const display =
              by === 'route'
                ? routeLabel(row)
                : by === 'day'
                  ? formatDayShort(row.label)
                  : row.label;
            return (
              <tr
                key={`${row.label}-${idx}`}
                // Zebra striping: these tables are all numbers, and a 30-row Day
                // breakdown is unreadable without a row anchor.
                className={`border-t border-gray-100 hover:bg-primary-500/5 ${
                  idx % 2 ? 'bg-surface-sunken' : ''
                }`}
              >
                <td className="px-3 py-1.5 text-slate-800" title={row.label}>
                  <span className="inline-flex items-center gap-1.5">
                    {display || '—'}
                    {by === 'route' && onFilterRoute && (
                      <button
                        type="button"
                        onClick={() =>
                          onFilterRoute(
                            isGroupingRoute(row)
                              ? { groupingId: row.groupingId }
                              : {
                                  dispatchLocation: row.dispatchLocation ?? '',
                                  receiptLocation: row.receiptLocation ?? '',
                                }
                          )
                        }
                        title={`Filter the whole page to ${display}`}
                        aria-label={`Filter to this route: ${display}`}
                        className="text-gray-300 hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 rounded"
                      >
                        <FaFilter className="text-[9px]" />
                      </button>
                    )}
                  </span>
                </td>
                <td className={`${num} text-slate-800`}>{formatCount(row.tickets)}</td>
                <td className={`${num} text-gray-400 whitespace-nowrap`}>
                  {formatCount(row.dispatchTickets)} / {formatCount(row.receiptTickets)}
                </td>
                <td className={`${num} font-semibold text-slate-800`}>
                  {formatTonnes(row.nettTonnes)}
                </td>
                <td className={`${num} text-gray-500`}>{formatTonnes(row.dispatchTonnes)}</td>
                <td className={`${num} text-gray-500`}>{formatTonnes(row.receiptTonnes)}</td>
                <td className={`${num} ${varianceToneClass(pct)}`}>
                  {formatTonnes(row.varianceTonnes)}
                  {pct !== null && (
                    <span className="ml-1 text-[10px] opacity-70">{formatPct(pct)}</span>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-gray-400 text-xs">
                No breakdown data
              </td>
            </tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-slate-800">
              <td className="px-3 py-1.5">Total · {formatCount(rows.length)} rows</td>
              <td className={num}>{formatCount(totals.tickets)}</td>
              <td className={`${num} text-gray-500 font-normal whitespace-nowrap`}>
                {formatCount(totals.dispatchTickets)} / {formatCount(totals.receiptTickets)}
              </td>
              <td className={num}>{formatTonnes(totals.nettTonnes)}</td>
              <td className={num}>{formatTonnes(totals.dispatchTonnes)}</td>
              <td className={num}>{formatTonnes(totals.receiptTonnes)}</td>
              <td className={`${num} ${varianceToneClass(totalPct)}`}>
                {formatTonnes(totalVariance)}
                {totalPct !== null && (
                  <span className="ml-1 text-[10px] opacity-70">{formatPct(totalPct)}</span>
                )}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

const DayChart = ({ rows }) => {
  const t = useThemeTokens();
  const data = rows.map((r) => ({
    day: r.label,
    dayLabel: formatDayShort(r.label),
    dispatchTonnes: Number(r.dispatchTonnes) || 0,
    receiptTonnes: Number(r.receiptTonnes) || 0,
  }));

  if (data.length === 0) return null;

  return (
    <div className="h-56 w-full px-2 pt-3" data-testid="period-summary-day-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
          <XAxis
            dataKey="dayLabel"
            stroke={t.axis}
            tick={{ fill: t.axisLabel, fontSize: 10 }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke={t.axis}
            tick={{ fill: t.axisLabel, fontSize: 10 }}
            tickFormatter={formatAxisNumber}
            width={44}
          />
          <Tooltip content={<RechartsDayTooltip />} cursor={{ fill: t.cursor }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            dataKey="dispatchTonnes"
            name="Dispatch t"
            fill={DISPATCH_COLOR}
            radius={[2, 2, 0, 0]}
          />
          <Bar
            dataKey="receiptTonnes"
            name="Receipt t"
            fill={RECEIPT_COLOR}
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * @param {object}   props
 * @param {string}   props.groupKey       the Ref No / Order No being expanded
 * @param {object}   props.query          { startDate, endDate, groupBy, ticketType } — memoised by the page
 * @param {object}   props.breakdowns     page-level cache: { `${key}|${by}`: { rows } | { error } | 'loading' }
 * @param {Function} props.onLoadBreakdown(key, by)
 */
const GroupDetail = ({ groupKey, groupBy, query, breakdowns, onLoadBreakdown, onFilterRoute }) => {
  const [tab, setTab] = useState(DEFAULT_TAB);

  // Grouped BY product, a product breakdown of one product is a single row.
  const tabs = useMemo(
    () => ALL_TABS.filter((t) => !(groupBy === 'product' && t.id === 'product')),
    [groupBy]
  );

  useEffect(() => {
    if (tab === 'tickets') return;
    onLoadBreakdown(groupKey, tab);
  }, [groupKey, tab, onLoadBreakdown]);

  const cacheKey = `${groupKey}|${tab}`;
  const entry = breakdowns[cacheKey];

  return (
    <div className="bg-surface-sunken border-t border-gray-200">
      <div
        className="flex items-center gap-1 px-3 pt-3 flex-wrap"
        role="tablist"
        aria-label="Group breakdown"
      >
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={`px-3 py-1.5 rounded-t-md text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
              tab === item.id
                ? 'bg-surface text-slate-800 border border-b-0 border-gray-200'
                : 'text-gray-500 hover:text-slate-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="bg-surface border border-gray-200 rounded-b-md rounded-tr-md mx-3 mb-3">
        {tab === 'tickets' ? (
          <TicketsTable groupKey={groupKey} query={query} />
        ) : entry === 'loading' || entry === undefined ? (
          <div className="flex items-center justify-center gap-2 py-8 text-xs text-gray-500">
            <LoadingSpinner size="sm" /> Loading breakdown…
          </div>
        ) : entry.error ? (
          <div
            role="alert"
            className="m-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center justify-between gap-3"
          >
            <span>{entry.error}</span>
            <button
              type="button"
              onClick={() => onLoadBreakdown(groupKey, tab, { force: true })}
              className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {tab === 'day' && <DayChart rows={entry.rows || []} />}
            <BreakdownTable by={tab} rows={entry.rows || []} onFilterRoute={onFilterRoute} />
          </>
        )}
      </div>
    </div>
  );
};

export default GroupDetail;
